import React from 'react';
import { Box, Drawer, IconButton, useMediaQuery, Dialog } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import SIDEBAR_CONFIG from './sidebarConfig';
import { FetchUserAssociatedStartups } from 'api';
import { SET_MENU } from '../../store/actions';
import { checkForDomain, hasPermission } from '../../utilities';
import { FetchLatestVersion } from 'api/version';
import { trackEvent } from 'utilities/mixpanel';
import { ReactComponent as CloseIcon } from 'assets/close_icon.svg';
import PricingPage from 'views/Subscription/PricingPageUpdated';
import SidebarHeader from './components/SidebarHeader';
import SidebarNavigation from './components/SidebarNavigation';
import SidebarFooter from './components/SidebarFooter';
import SwitchAccountsMenu from './components/SwitchAccountsMenu';
import LogoutConfirmationDialog from 'ui-components/popups/DeleteConfirmationPopup';
import { useLogout } from './hooks';
import {
  HomeIcon,
  BackrrPageIcon,
  RoundsIcon,
  InvestorPipelineIcon,
  PerformanceMetricsIcon,
  DataRoomsIcon,
  CapTableIcon,
  InvestorUpdatesIcon,
  InvestorNetworkIcon,
  AIInsightsIcon,
  ResourcesIcon,
  SettingsIcon,
  BillingPlanIcon,
} from './components/SidebarIcons';
import { Settings } from 'lucide-react';

const ManageSubscriptionModal = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpen, setIsOpen] = React.useState(searchParams.get('upgrade') === 'true');

  React.useEffect(() => {
    setIsOpen(searchParams.get('upgrade') === 'true');
  }, [searchParams]);

  const handleClose = () => {
    setIsOpen(false);
    searchParams.delete('upgrade');
    searchParams.delete('plan_type');
    searchParams.delete('billing_period');
    setSearchParams(searchParams);
  };

  return (
    <Dialog
      sx={(theme) => ({
        '& .MuiPaper-root': {
          // backgroundColor: theme.palette.customColors.yellow_bg,
        },
      })}
      onClose={handleClose}
      maxWidth="sm"
      fullScreen
      open={isOpen}
    >
      <IconButton sx={{ position: 'absolute', top: 20, right: 20, padding: 0 }}>
        <CloseIcon width={40} height={40} onClick={handleClose} />
      </IconButton>
      <PricingPage />
    </Dialog>
  );
};

const NewSidebar = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { refetch: refetchLatestVersion } = FetchLatestVersion();
  const [startup_id, setStartupID] = React.useState(
    Cookies.get('X-ST-ID') || null
  );
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const theme = useTheme();
  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    data: userAssociatedStartups,
    refetch: refetchUserAssociatedStartups,
  } = FetchUserAssociatedStartups();

  const userAccess = useSelector((state) => state?.permissions);
  const startupDetail = useSelector((state) => state?.startupDetails?.data);
  const {
    is_subscribed,
    current_plan,
    subscription_details,
    loading_plans,
    loading_subscription,
  } = useSelector((state) => state?.startupDetails);

  const open = useSelector((state) => state.customization.opened);

  const toggleDrawer = () => {
    refetchLatestVersion();
    dispatch({ type: SET_MENU, opened: !open });
  };

  const handleSetSearchURL = (name, value) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(name, value);
    setSearchParams(newParams);
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
    refetchUserAssociatedStartups();
  };

  const handleMenuClose = ({ _id, startup_name } = {}) => {
    if (_id && startup_id !== _id) {
      Cookies.set('X-ST-ID', _id, { domain: checkForDomain() });
      if (window.location.host?.includes('localhost')) {
        Cookies.set('X-ST-ID', _id);
      }
      trackEvent('Switch Startup Profile', { startup_name });
      setStartupID(_id);
    }
    setAnchorEl(null);
    if (_id && startup_id !== _id) {
      window.location.reload();
    }
  };

  const performLogOut = useLogout(startupDetail, userAccess);
  const [openLogoutConfirmation, setOpenLogoutConfirmation] =
    React.useState(false);

  const handleLogOut = () => {
    setAnchorEl(null);
    setOpenLogoutConfirmation(true);
  };

  const handleLogoutConfirmationClose = () => {
    setOpenLogoutConfirmation(false);
  };

  const handleConfirmLogOut = () => {
    setOpenLogoutConfirmation(false);
    performLogOut();
  };

  const menuConfig = buildMenuConfig(
    userAccess,
    is_subscribed,
    handleSetSearchURL
  );

  return (
    <>
      <ManageSubscriptionModal />
      <Box
        component="nav"
        aria-label="sidebar navigation"
        sx={{
          width: matchesUpMd
            ? sidebarCollapsed
              ? `${SIDEBAR_CONFIG.desktop.collapsed}px`
              : `${SIDEBAR_CONFIG.desktop.expanded}px`
            : 'auto',
          flexShrink: 0,
          transition: 'width 0.3s ease',
        }}
      >
        <Drawer
          variant={matchesUpMd ? 'permanent' : 'temporary'}
          open={matchesUpMd ? false : open}
          onClose={toggleDrawer}
          sx={{
            height: '100dvh',
            '& .MuiDrawer-paper': {
              width: matchesUpMd
                ? sidebarCollapsed
                  ? `${SIDEBAR_CONFIG.desktop.collapsed}px`
                  : `${SIDEBAR_CONFIG.desktop.expanded}px`
                : SIDEBAR_CONFIG.mobile.temporary,
              height: '100%',
              backgroundColor: '#000000',
              color: '#FFFFFF',
              borderRight: 'none',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              transition: 'width 0.3s ease',
              position: matchesUpMd ? 'relative' : 'fixed',
              padding: '0.5rem',
            },
          }}
        >
          <SidebarHeader
            sidebarCollapsed={sidebarCollapsed}
            matchesUpMd={matchesUpMd}
            toggleDrawer={toggleDrawer}
            navigate={navigate}
            loading_plans={loading_plans}
            loading_subscription={loading_subscription}
            subscription_details={subscription_details}
            is_subscribed={is_subscribed}
            current_plan={current_plan}
            startupDetail={startupDetail}
            handleSetSearchURL={handleSetSearchURL}
            userAccess={userAccess}
          />

          <SidebarNavigation
            menuConfig={menuConfig}
            sidebarCollapsed={sidebarCollapsed}
            matchesUpMd={matchesUpMd}
            toggleDrawer={toggleDrawer}
          />

          <SidebarFooter
            sidebarCollapsed={sidebarCollapsed}
            matchesUpMd={matchesUpMd}
            toggleDrawer={toggleDrawer}
            userAccess={userAccess}
            startupDetail={startupDetail}
            menuConfig={menuConfig}
            handleAvatarClick={handleAvatarClick}
            handleLogOut={handleLogOut}
            anchorEl={anchorEl}
          />

          <SwitchAccountsMenu
            anchorEl={anchorEl}
            isMenuOpen={Boolean(anchorEl)}
            handleMenuClose={handleMenuClose}
            userAssociatedStartups={userAssociatedStartups}
            startup_id={startup_id}
            handleLogOut={handleLogOut}
          />
        </Drawer>
      </Box>

      <LogoutConfirmationDialog
        open={openLogoutConfirmation}
        handleClose={handleLogoutConfirmationClose}
        handleSubmission={handleConfirmLogOut}
        heading="Confirm Logout"
        subheading="Are you sure you want to log out of your account?"
        ActionText="Logout"
      />
    </>
  );
};

function buildMenuConfig(userAccess, is_subscribed, handleSetSearchURL) {
  return {
    home: [
      // {
      //   label: 'Home',
      //   icon: HomeIcon,
      //   path: '/',
      //   permission: true,
      //   iconStyle: { width: '22px', height: '22px', marginLeft: '-2px' },
      // },
    ],
    fundraise: [
      {
        label: 'Backrr Page',
        icon: BackrrPageIcon,
        path: '/profile',
        permission: hasPermission(
          'profile',
          'view',
          userAccess?.userPermissions
        ),
      },
      {
        label: 'Rounds',
        icon: RoundsIcon,
        permission: hasPermission(
          'funding_rounds',
          'view',
          userAccess?.userPermissions
        ),
        children: [
          {
            label: 'Open Round',
            path: '/active-round',
            permission: hasPermission(
              'funding_rounds',
              'view',
              userAccess?.userPermissions
            ),
            // The /active-round page resolves the open round itself; clear any
            // round left over from a previously viewed round so it can't render
            // stale details while that lookup is in flight.
            onNavigate: () => sessionStorage.removeItem('round_id'),
          },
          {
            label: 'All Rounds',
            path: '/rounds',
            permission: hasPermission(
              'funding_rounds',
              'view',
              userAccess?.userPermissions
            ),
          },
        ],
        iconStyle: { width: '22px', height: '22px', marginLeft: '-2px' },
      },
      {
        label: 'Investor Pipeline',
        icon: InvestorPipelineIcon,
        path: '/pipeline',
        permission: hasPermission(
          'pipeline',
          'view',
          userAccess?.userPermissions
        ),
      },
      {
        label: 'Performance Metrics',
        icon: PerformanceMetricsIcon,
        path: '/performance',
        permission: hasPermission(
          'performance',
          'view',
          userAccess?.userPermissions
        ),
        iconStyle: { width: '22px', height: '22px', marginLeft: '-2px' },
      },
      {
        label: 'Data Rooms',
        icon: DataRoomsIcon,
        permission: hasPermission(
          'drive',
          'documents',
          userAccess?.userPermissions
        ),
        children: [
          {
            label: 'All Data Rooms',
            path: '/data-rooms',
            permission: hasPermission(
              'drive',
              'data_rooms',
              userAccess?.userPermissions
            ),
          },
          {
            label: 'All Documents',
            path: '/documents',
            permission: hasPermission(
              'drive',
              'documents',
              userAccess?.userPermissions
            ),
          },
        ],
      },
    ],
    relationships: [
      {
        label: 'Cap Table',
        path: '/captable',
        icon: CapTableIcon,
        permission: hasPermission(
          'captable',
          'view',
          userAccess?.userPermissions
        ),
      },
      {
        label: 'Investor Updates',
        icon: InvestorUpdatesIcon,
        permission: hasPermission(
          'reports',
          'view',
          userAccess?.userPermissions
        ),
        children: [
          {
            label: 'Create Update',
            path: '/reports/create_report',
            permission: hasPermission(
              'reports',
              'add',
              userAccess?.userPermissions
            ),
          },
          {
            label: 'All Updates',
            path: '/reports',
            permission: hasPermission(
              'reports',
              'view',
              userAccess?.userPermissions
            ),
          },
          // { label: 'Investor List', path: '/' },
        ],
      },
    ],
    intelligence: [
      {
        label: 'Investor Network',
        icon: InvestorNetworkIcon,
        path: '/investor-network',
        permission: hasPermission(
          'investor_network',
          'view',
          userAccess?.userPermissions
        ),
      },
      {
        label: 'AI Insights',
        icon: AIInsightsIcon,
        path: '/tools',
        permission: hasPermission(
          'ai_insights',
          'view',
          userAccess?.userPermissions
        ),
      },
      {
        label: 'Resources',
        icon: ResourcesIcon,
        path: '/resources',
        permission: hasPermission(
          'resources',
          'view',
          userAccess?.userPermissions
        ),
      },
    ],
    bottom: [
      {
        label: 'Settings',
        icon: () => <Settings width={17} height={17} />,
        // icon: SettingsIcon,
        path: '/settings',
        permission: hasPermission(
          'settings',
          'manage_users',
          userAccess?.userPermissions
        ),
      },
      {
        label: 'Billing & Plan',
        icon: BillingPlanIcon,
        permission: userAccess?.isOwner,  // Show only to owners
        children: [
          {
            label: 'Orders & Invoices',
            path: '/orders-invoices',
            permission: userAccess?.isOwner,  // Show only to owners
          },
          {
            label: is_subscribed ? 'Manage Subscription' : 'Upgrade Plan',
            path: is_subscribed ? '/manage_subscription' : '#',
            permission: userAccess?.isOwner,  // Show only to owners
            onNavigate: () => {
              if (!is_subscribed) {
                handleSetSearchURL('upgrade', 'true');
              }
            },
          },
        ],
      },
    ],
  };
}

export default NewSidebar;
